import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import {
  BarChart3,
  Boxes,
  CalendarClock,
  ChefHat,
  CreditCard,
  LayoutGrid,
  ShoppingCart,
  UtensilsCrossed,
} from "@fleet/icons";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Logo } from "@/components/ui/logo";

/**
 * Comandero domain dashboard: the landing route. It surfaces quick access to the
 * core POS workspaces (sell, kitchen, menu, floor, cashbox, reports) instead of
 * the inherited task smart-views/lists. It is intentionally lightweight and
 * local-first: no PowerSync subscriptions, renders instantly with the shell.
 *
 * Visual identity: warm gastronomic accent ("ember") with a hero banner. Every
 * shortcut icon sits inside a solid `bg-accent`/`text-accent-foreground` chip so
 * it stays legible across every theme (avoids the accent-on-tinted-surface gotcha).
 */
export function Dashboard() {
  const { t } = useI18n();
  const session = useSession();

  const firstName = (): string | undefined => session.data?.user.name?.split(" ")[0];

  const SHORTCUTS = [
    {
      to: "/pos",
      label: msg`Point of sale`,
      hint: msg`Take orders and charge`,
      icon: ShoppingCart,
    },
    {
      to: "/kds",
      label: msg`Kitchen display`,
      hint: msg`Fire and track tickets`,
      icon: ChefHat,
    },
    { to: "/menu", label: msg`Menu`, hint: msg`Dishes and pricing`, icon: UtensilsCrossed },
    { to: "/floor", label: msg`Floor`, hint: msg`Tables and seating`, icon: LayoutGrid },
    { to: "/cashbox", label: msg`Cashbox`, hint: msg`Shifts and till`, icon: CreditCard },
    { to: "/inventory", label: msg`Inventory`, hint: msg`Stock and supplies`, icon: Boxes },
    {
      to: "/reservations",
      label: msg`Reservations`,
      hint: msg`Bookings and waitlist`,
      icon: CalendarClock,
    },
    {
      to: "/reports",
      label: msg`Reports`,
      hint: msg`Sales and performance`,
      icon: BarChart3,
    },
  ] as const;

  return (
    <div
      class="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 [scrollbar-gutter:stable]"
      style={{
        "overflow-anchor": "none",
        "will-change": "scroll-position",
      }}
    >
      <div class="mx-auto max-w-7xl space-y-10">
        {/* Hero banner — warm ember gradient with the Comandero mark. */}
        <header class="relative overflow-hidden rounded-3xl border border-border/40 bg-card/40 p-6 md:p-9">
          <div
            aria-hidden="true"
            class="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
          />
          <div
            aria-hidden="true"
            class="pointer-events-none absolute -bottom-24 -left-10 h-56 w-56 rounded-full bg-warning/15 blur-3xl"
          />
          <div class="relative flex flex-col gap-5">
            <Logo size="sm" />
            <div class="space-y-2">
              <h1 class="text-4xl font-extrabold tracking-tight md:text-5xl">
                <Show when={session.isAuthenticated()} fallback={t(msg`Run your restaurant`)}>
                  {(() => {
                    const name = firstName();
                    return name ? t(msg`Hello, ${name}`) : t(msg`Welcome back`);
                  })()}
                </Show>
              </h1>
              <Show
                when={session.isAuthenticated()}
                fallback={
                  <p class="max-w-lg text-base text-muted-foreground">
                    {t(msg`You are in visitor mode. Sign in to sync your restaurant and sell.`)}
                  </p>
                }
              >
                <p class="max-w-lg text-base text-muted-foreground">
                  {t(msg`Your service, in one place — sell, cook, and settle the till.`)}
                </p>
              </Show>
            </div>
          </div>
        </header>

        <section class="space-y-4">
          <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground/70">
            {t(msg`Quick access`)}
          </h2>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <For each={SHORTCUTS}>
              {(item) => (
                <Link
                  to={item.to}
                  class="group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-border/40 bg-card/40 p-4 transition-[transform,border-color,background-color,box-shadow] duration-200 will-change-transform hover:-translate-y-0.5 hover:border-accent/50 hover:bg-card/70 hover:shadow-lg hover:shadow-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98]"
                  data-testid="dashboard-shortcut"
                >
                  <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-sm shadow-accent/30 transition-transform duration-200 group-hover:scale-105">
                    <item.icon class="h-5.5 w-5.5" />
                  </span>
                  <span class="min-w-0 flex flex-col">
                    <span class="truncate text-sm font-semibold text-foreground">
                      {t(item.label)}
                    </span>
                    <span class="truncate text-xs text-muted-foreground">{t(item.hint)}</span>
                  </span>
                </Link>
              )}
            </For>
          </div>
        </section>
      </div>
    </div>
  );
}
